import { useState, useEffect, lazy, Suspense } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  AdminRoutes,
  PrivateRoutes,
  PublicRoutes,
} from "./middleware/RouteMiddleware";
import { CmsContextProvider } from "./Context/CmsContext";
import CustomNavbar from "./Components/Navbar";
import Footer from "./Components/Footer";
import ScrollToTop from "./Components/ScrollToTop";
import BackToTopButton from "./Components/BackToTopButton";
import Logo from "./assets/logo.png";
import { ToastContainer } from "react-toastify";
import ReactGA from "react-ga4";

// Lazy load pages for better performance
const Home = lazy(() => import("./Pages/Home"));
const Trading = lazy(() => import("./Pages/Trading"));
const Platforms = lazy(() => import("./Pages/Platforms"));
const Promotions = lazy(() => import("./Pages/Promotions"));
const Bonus = lazy(() => import("./Pages/Bonus"));
const EconomicCalendar = lazy(() => import("./Pages/EconomicCalendar"));
const TradingCalculators = lazy(() => import("./Pages/TradingCalculators"));
const Ebooks = lazy(() => import("./Pages/Ebooks"));
const BeginnerGuides = lazy(() => import("./Pages/BeginnerGuides"));
const JoinCommunity = lazy(() => import("./Pages/JoinCommunity"));
const TradersSociety = lazy(() => import("./Pages/TradersSociety"));
const About = lazy(() => import("./Pages/About"));
const Partnership = lazy(() => import("./Pages/Partnership"));
const Transfers = lazy(() => import("./Pages/Transfers"));
const NotFound = lazy(() => import("./Pages/NotFound"));
const Micro = lazy(() => import("./Pages/account-types/micro"));
const RawSpread = lazy(() => import("./Pages/account-types/RawSpread"));
const Standard = lazy(() => import("./Pages/account-types/Standard"));
const Prime = lazy(() => import("./Pages/account-types/prime"));
const ReferFriend = lazy(() => import("./Pages/ReferFriend"));
const MetaTrader = lazy(() => import("./Pages/MetaTrader"));
const Forex = lazy(() => import("./Pages/account-types/Forex"));
const Indices = lazy(() => import("./Pages/account-types/indices"));
const Energies = lazy(() => import("./Pages/account-types/Energies"));
const Metals = lazy(() => import("./Pages/account-types/Metals"));
const Cryptocurrency = lazy(() => import("./Pages/account-types/Cryptocurrency"));
const Execution = lazy(() => import("./Pages/account-types/execution-policy"));
const Spreads = lazy(() => import("./Pages/account-types/Spreads"));
const Margin = lazy(() => import("./Pages/account-types/Margin"));
const BenefitsHome = lazy(() => import("./Components/BenefitsHome"));
const AddBlog = lazy(() => import("./Pages/Blog/AddBlog"));
const AbetApp = lazy(() => import("./Pages/AbetApp"));
const FAQ = lazy(() => import("./Components/FAQ"));
const Contact = lazy(() => import("./Pages/Contact"));
const PrivacyPolicy = lazy(() => import("./Pages/PrivacyPolicy"));
const LegalDocuments = lazy(() => import("./Pages/LegalDocuments"));
const RegulationSafety = lazy(() => import("./Pages/RegulationSafety"));
const AwardsCertificates = lazy(() => import("./Pages/AwardsCertificates"));
const RiskDisclosure = lazy(() => import("./Components/Risk"));
const BlogDetail = lazy(() => import("./Pages/Blog/BlogDetail"));
const Blogs = lazy(() => import("./Pages/Blogs"));
const BecomePartner = lazy(() => import("./Pages/BecomePartner"));
const AccountTypes = lazy(() => import("./Pages/account-types/Accounttypes"));
const ForexDictionary = lazy(() => import("./Pages/ForexDic"));
const Login = lazy(() => import("./Pages/Authentication/Login"));
const AddUser = lazy(() => import("./Pages/User/AddUser"));
const UserList = lazy(() => import("./Pages/User/UserList"));
const BlogList = lazy(() => import("./Pages/Blog/BlogList"));
const Forexdictionary = lazy(() => import("./Pages/ForexDic"));
const Dashboard = lazy(() => import("./Pages/Dashboard"));
const ContentManager = lazy(() => import("./Pages/ContentManager"));

const TRACKING_ID = "G-E1GLTK5PRC";
ReactGA.initialize(TRACKING_ID);

const LayoutWrapper = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname });
  }, [location]);
  const hideNavbarFooter = ["/login"].includes(
    location.pathname.toLocaleLowerCase()
  );
  return (
    <>
      <ScrollToTop />
      <BackToTopButton />
      {!hideNavbarFooter && <CustomNavbar />}
      {<ToastContainer
        position="top-right"
        autoClose={1200}
        hideProgressBar
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover={false}
        theme="colored"
      />}
      <main>{children}</main>
      {!hideNavbarFooter && <Footer />}
    </>
  );
};

const Preloader = () => {
  return (
    <div className="preloader">
      <div className="preloader-content">
        <img src={Logo} alt="Logo" />
        <p className="video-description">Trade Anytime Anywhere</p>
        <div className="spinner"></div>
      </div>
    </div>
  );
};

const AppWrapper = () => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const { i18n } = useTranslation();

  // Set initial language and direction on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'EN';
    i18n.changeLanguage(savedLanguage);
    const isRTL = savedLanguage === 'AR' || savedLanguage === 'UR';
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = savedLanguage.toLowerCase();
  }, [i18n]);

  // Loader disabled
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     setLoading(false);
  //   }, 3000);

  //   return () => clearTimeout(timer);
  // }, []);

  // useEffect(() => {
  //   setLoading(true);
  //   const timer = setTimeout(() => {
  //     setLoading(false);
  //   }, 1000);

  //   return () => clearTimeout(timer);
  // }, [location.pathname]);

  return (
    <>
      {loading && <Preloader />}
      <div className={`app-content ${loading ? "content-hidden" : ""}`}>
        <LayoutWrapper>
          <Suspense fallback={
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '400px' 
            }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          }>
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/trading" element={<Trading />} />
            <Route path="/platforms" element={<Platforms />} />
            <Route path="/promotions" element={<Promotions />} />
            <Route path="/bonus" element={<Bonus />} />
            <Route path="/economic-calendar" element={<EconomicCalendar />} />
            <Route path="/trading-calculators" element={<TradingCalculators />} />
            <Route path="/ebooks" element={<Ebooks />} />
            <Route path="/beginner-guides" element={<BeginnerGuides />} />
            <Route path="/join-community" element={<JoinCommunity />} />
            <Route path="/traders-society" element={<TradersSociety />} />
            <Route path="/about" element={<About />} />
            <Route path="/partnership" element={<Partnership />} />
            <Route path="/transfers" element={<Transfers />} />
            <Route path="/micro" element={<Micro />} />
            <Route path="/raw-spread" element={<RawSpread />} />
            <Route path="/standard" element={<Standard />} />
            <Route path="/forex" element={<Forex />} />
            <Route path="/indices" element={<Indices />} />
            <Route path="/energies" element={<Energies />} />
            <Route path="/metals" element={<Metals />} />
            <Route path="/cryptocurrency" element={<Cryptocurrency />} />
            <Route path="/prime" element={<Prime />} />
            <Route path="/execution-policy" element={<Execution />} />
            <Route path="/spreads" element={<Spreads />} />
            <Route path="/margin-leverage" element={<Margin />} />
            <Route path="/refer-a-friend" element={<ReferFriend />} />
            <Route path="/meta-trader-5" element={<MetaTrader />} />
            <Route path="/benefithome" element={<BenefitsHome />} />
            <Route path="/benefits" element={<BenefitsHome />} />
            <Route path="/AbetApp" element={<AbetApp />} />
            <Route path="/faqs" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/legal-documents" element={<LegalDocuments />} />
            <Route path="/trusted-safety" element={<RegulationSafety />} />
            <Route path="/regulation-safety" element={<Navigate to="/trusted-safety" replace />} />
            <Route path="/account-types" element={<Navigate to="/account-type" replace />} />
            <Route path="/margin" element={<Navigate to="/margin-leverage" replace />} />
            <Route path="/metatrader" element={<Navigate to="/meta-trader-5" replace />} />
            <Route path="/refer-friend" element={<Navigate to="/refer-a-friend" replace />} />
            <Route path="/become-partner" element={<Navigate to="/partner" replace />} />
            <Route path="/abet-app" element={<Navigate to="/AbetApp" replace />} />
            <Route path="/awards-certificates" element={<AwardsCertificates />} />
            <Route path="/risk" element={<RiskDisclosure />} />
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/blog-details/:slug" element={<BlogDetail />} />
            <Route path="/partner" element={<BecomePartner />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/account-type" element={<AccountTypes />} />
            <Route path="/forex-dictionary" element={<ForexDictionary />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forex-dictionary" element={<Forexdictionary />} />
            <Route path="*" element={<NotFound />} />
            <Route element={<PublicRoutes />}>
              <Route path="/login" element={<Login />} />
            </Route>
            <Route element={<PrivateRoutes />}>
              <Route path="/add-blog" element={<AddBlog />} />
              <Route path="/add-blog/:slug" element={<AddBlog />} />
            </Route>
            <Route element={<AdminRoutes />}>
              <Route path="/add-user" element={<AddUser />} />
              <Route path="/add-user/:id" element={<AddUser />} />
              <Route path="/user-list" element={<UserList />} />
              <Route path="/blog-list" element={<BlogList />} />
              <Route path="/content-manager" element={<ContentManager />} />
            </Route>
          </Routes>
          </Suspense>
        </LayoutWrapper>
      </div>
    </>
  );
};

function App() {
  return (
    <Router>
      <CmsContextProvider>
        <AppWrapper />
      </CmsContextProvider>
    </Router>
  );
}

export default App;
